import { Head, Link } from '@inertiajs/react';
import { ArrowLeft } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { AdminFeedbacksShow } from '@/types';

const PARTICIPATION_LABELS: Record<string, string> = {
  yes: 'Sí',
  no: 'No',
  maybe: 'Tal vez',
};

const SPONSOR_WORK_LABELS: Record<string, string> = {
  yes: 'Sí',
  no: 'No',
  already_did: 'Ya lo hice',
};

const STARTUP_INTENT_LABELS: Record<string, string> = {
  yes: 'Sí',
  no: 'No',
  already_building: 'Ya estoy construyendo',
};

const FUNDING_LABELS: Record<string, string> = {
  bootstrapped: 'Bootstrapped',
  vc: 'VC',
  other: 'Otro',
};

const AMBITION_LABELS: Record<string, string> = {
  up_to_100k: 'Hasta $100k',
  '100k_to_1m': '$100k - $1M',
  '1m_to_10m': '$1M - $10M',
  '10m_plus': '$10M+',
  not_sure: 'No estoy seguro',
};

const USAGE_PERMISSION_LABELS: Record<string, string> = {
  yes_with_name: 'Sí, con nombre',
  yes_anonymous: 'Sí, anónimo',
  no: 'No',
};

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-muted-foreground text-xs uppercase tracking-wide">
        {label}
      </div>
      <div className="mt-1 text-sm">{children}</div>
    </div>
  );
}

function LongText({ value }: { value: string | null }) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return <p className="whitespace-pre-wrap text-sm">{value}</p>;
}

export default function Show({
  feedback,
  qualityLabels,
  eventSlug,
  title,
}: AdminFeedbacksShow) {
  return (
    <>
      <Head title={title} />
      <div className="container mx-auto max-w-4xl space-y-6 py-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <Button variant="ghost" size="sm" asChild className="mb-2">
              <Link href={`/admin/${eventSlug}/feedback`}>
                <ArrowLeft className="mr-1 h-4 w-4" />
                Back to feedback
              </Link>
            </Button>
            <h1 className="font-bold text-3xl tracking-tight">
              {feedback.hackerFullName}
            </h1>
            <p className="text-muted-foreground text-sm">
              {feedback.hackerEmail}
              {feedback.hackerGithub ? (
                <>
                  {' · '}
                  <a
                    href={
                      feedback.hackerGithub.startsWith('http')
                        ? feedback.hackerGithub
                        : `https://github.com/${feedback.hackerGithub.replace(/^@/, '')}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    GitHub
                  </a>
                </>
              ) : null}
            </p>
            <p className="text-muted-foreground text-xs">
              Submitted {new Date(feedback.createdAt).toLocaleString()}
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Overview</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Field label="Overall rating">
              <span className="font-semibold text-2xl">
                {feedback.overallRating}
                <span className="text-muted-foreground text-sm">/10</span>
              </span>
            </Field>
            <Field label="NPS">
              <span className="font-semibold text-2xl">
                {feedback.npsScore}
                <span className="text-muted-foreground text-sm">/10</span>
              </span>
            </Field>
            <Field label="Would participate again">
              {PARTICIPATION_LABELS[feedback.participationIntent] ??
                feedback.participationIntent}
            </Field>
            <Field label="Mentor rating">
              {feedback.mentorRating != null ? (
                <>
                  {feedback.mentorRating}
                  <span className="text-muted-foreground text-sm">/10</span>
                </>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </Field>
            <Field label="Aura rating">
              {feedback.auraRating != null ? (
                <>
                  {feedback.auraRating}
                  <span className="text-muted-foreground text-sm">/10</span>
                </>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </Field>
            <Field label="Team">
              {feedback.teamId && feedback.teamSlug ? (
                <Link
                  href={`/admin/${eventSlug}/teams/${feedback.teamSlug}`}
                  className="text-blue-600 hover:text-blue-800"
                >
                  {feedback.teamSlug}
                </Link>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </Field>
            <Field label="Project">
              {feedback.projectSlug && feedback.projectName ? (
                <Link
                  href={`/projects/${feedback.projectSlug}`}
                  className="text-blue-600 hover:text-blue-800"
                >
                  {feedback.projectName}
                </Link>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </Field>
            <Field label="Usage permission">
              <Badge variant="outline">
                {USAGE_PERMISSION_LABELS[feedback.feedbackUsagePermission] ??
                  feedback.feedbackUsagePermission}
              </Badge>
            </Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Event quality (1–5)</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {Object.entries(feedback.eventQualityRatings).map(([key, value]) => (
              <div key={key} className="rounded border bg-background p-3">
                <div className="text-muted-foreground text-xs">
                  {qualityLabels[key] ?? key}
                </div>
                <div className="font-semibold text-lg">
                  {value}
                  <span className="text-muted-foreground text-sm">/5</span>
                </div>
              </div>
            ))}
            {feedback.hackRacerRating != null && (
              <div className="rounded border bg-background p-3">
                <div className="text-muted-foreground text-xs">
                  Platanus Hack Racer
                </div>
                <div className="font-semibold text-lg">
                  {feedback.hackRacerRating}
                  <span className="text-muted-foreground text-sm">/5</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Qualitative feedback</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field label="Best part">
              <LongText value={feedback.bestPart} />
            </Field>
            <Field label="Worst part">
              <LongText value={feedback.worstPart} />
            </Field>
            <Field label="Suggestions">
              <LongText value={feedback.suggestions} />
            </Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Sponsors</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field label="Unaided recall">
              <LongText value={feedback.sponsorUnaidedRecall} />
            </Field>
            <Field label="Interacted with">
              {feedback.sponsorsInteracted &&
              feedback.sponsorsInteracted.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {feedback.sponsorsInteracted.map((s) => (
                    <Badge key={s} variant="secondary">
                      {s}
                    </Badge>
                  ))}
                </div>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </Field>
            <Field label="Would work for a sponsor">
              {feedback.sponsorWorkIntent
                ? (SPONSOR_WORK_LABELS[feedback.sponsorWorkIntent] ??
                  feedback.sponsorWorkIntent)
                : '—'}
            </Field>
            <Field label="Sponsor comments">
              <LongText value={feedback.sponsorComments} />
            </Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Future</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Startup intent">
              {STARTUP_INTENT_LABELS[feedback.startupIntent] ??
                feedback.startupIntent}
            </Field>
            <Field label="Funding preference">
              {feedback.fundingPreference
                ? (FUNDING_LABELS[feedback.fundingPreference] ??
                  feedback.fundingPreference)
                : '—'}
            </Field>
            <Field label="Ambition">
              {feedback.startupAmbition
                ? (AMBITION_LABELS[feedback.startupAmbition] ??
                  feedback.startupAmbition)
                : '—'}
            </Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Extras</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field label="How they heard about it">
              <LongText value={feedback.howHeardAbout} />
            </Field>
            <Field label="Additional comments">
              <LongText value={feedback.additionalComments} />
            </Field>
            <Field label="Media uploads">
              {feedback.mediaUrls && feedback.mediaUrls.length > 0 ? (
                <ul className="space-y-1 text-sm">
                  {feedback.mediaUrls.map((url, i) => (
                    <li key={url}>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Archivo {i + 1}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </Field>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
